import { Button, Card, CardContent } from '@h2hspark/ui';
import * as React from 'react';

import {
  useMatchingPreferences,
  useRegistrationVocabularies,
  useSaveMatchingPreferences,
} from './api';
import { MATCHING_FIELD_ATTRIBUTES, MatchingFieldsPanel } from './matching-fields-panel';

/**
 * The second mount point (`MEM-045` AC-3, AC-4): the four optional matching fields, editable at
 * any time by a member who skipped them at registration.
 *
 * ## What this is not
 *
 * It is **not** `MEM-009`'s matching-preferences screen. That story wants every matching input —
 * interests, goal, connection intent, format and social comfort — on one screen reachable from
 * both the profile and My Account, and neither `MEM-005` (the profile) nor `MEM-007` (My Account)
 * is built. This is the four optional fields and nothing else, mounted on the one member surface
 * that exists today, and `MEM-009` will absorb it rather than replace it: the fields, the copy and
 * the mount table are all in `@h2hspark/contracts` precisely so that moving them is a new mount
 * row rather than a rewrite.
 *
 * ## Explicit saving
 *
 * One Save button, and nothing writes without it — the house rule since the organizer wizard's
 * autosave was removed on 2026-08-19, and `MEM-007` AC-4 restates it for this surface. There is no
 * timer, no blur handler and no effect that writes.
 */
export interface MatchingPreferencesFormProps {
  /**
   * Attributes another control **on the same screen** already owns.
   *
   * One caller passes one: `MEM-009`'s matching-preferences screen renders social comfort as three
   * radio cards over "We'll match you into settings that feel right." (AC-5), and this panel would
   * otherwise draw a second control for `socialComfortTermId` a few centimetres below it — two
   * inputs for one attribute, with two Save buttons, where the last one pressed wins.
   *
   * **`MEM-045` AC-3 and `MEM-009` AC-5 both specify a control for this field and they specify
   * different ones**; that collision is real and is flagged on the pull request rather than
   * resolved silently. What is decided here is only which one renders when both are on screen, and
   * `MEM-009`'s wins because it is the screen-level story and the prototype's own rendering.
   *
   * Nothing about the mount table changes: `MATCHING_FIELDS_MOUNTS.preferences` still carries all
   * four fields, the `/matching-preferences` route still edits all four, and the registration
   * mounts are untouched. This is a difference held at a call site, in the same spirit as the
   * mount table itself.
   */
  readonly omitAttributes?: ReadonlyArray<string>;
}

export function MatchingPreferencesForm({
  omitAttributes = [],
}: MatchingPreferencesFormProps = {}): React.ReactElement | null {
  const preferences = useMatchingPreferences(true);
  const vocabularies = useRegistrationVocabularies(true);
  const save = useSaveMatchingPreferences();

  const [draft, setDraft] = React.useState<Record<string, string> | null>(null);
  const [saved, setSaved] = React.useState(false);

  // The one effect, and it reads rather than writes: the server's answer becomes the draft once,
  // when it arrives. A member's typing is never overwritten by a refetch because the draft is only
  // seeded while it is still `null`.
  React.useEffect(() => {
    if (draft === null && preferences.data !== undefined) {
      setDraft({ ...preferences.data.current });
    }
  }, [draft, preferences.data]);

  if (preferences.isError) {
    return (
      <p className="text-sm text-muted-foreground">
        We could not load these just now. Reload the page and they will be here.
      </p>
    );
  }
  if (draft === null || preferences.data === undefined) return null;

  const current = preferences.data.current;
  // An omitted attribute is not this form's to read or write — it is neither part of the dirty
  // check nor part of the patch, so the control that does own it cannot be undone from here.
  const attributes = MATCHING_FIELD_ATTRIBUTES.filter(
    (attribute) => !omitAttributes.includes(attribute),
  );
  const dirty = attributes.some(
    (attribute) => (draft[attribute] ?? '') !== (current[attribute] ?? ''),
  );

  // One white card, heading to Save, in the same style as the five section cards above it
  // (TW 44260222: Sumit chose option A on 2026-09-30, "ok with option A"). The prototype draws this
  // block on the page background under a divider; that is the deliberate deviation.
  return (
    <Card className="mb-5">
      <CardContent className="p-6">
        <form
          className="space-y-[18px]"
          onSubmit={(event) => {
            event.preventDefault();
            // `null` for an emptied field, so the server erases it rather than storing `''`. The
            // difference matters downstream: the scorer reads these attributes with an `undefined`
            // test, so an empty string would be a *present* answer that matches nobody.
            const patch: Record<string, string | null> = {};
            for (const attribute of attributes) {
              const next = draft[attribute] ?? '';
              if (next === (current[attribute] ?? '')) continue;
              patch[attribute] = next === '' ? null : next;
            }
            save.mutate(patch, {
              // The hook has already written the saved answers into the cache, so the reseed reads
              // them. Reseeding from a refetch's old answers is what put the previous Industry back
              // on screen (TW 44260267).
              onSuccess: () => {
                setSaved(true);
                setDraft(null);
              },
            });
          }}
        >
          <MatchingFieldsPanel
            mountPoint="preferences"
            values={draft}
            omitAttributes={omitAttributes}
            terms={vocabularies.data?.terms ?? {}}
            onChange={(attribute, value) => {
              setSaved(false);
              setDraft((previous) => ({ ...(previous ?? {}), [attribute]: value }));
            }}
            disabled={save.isPending}
          />

          <div className="flex items-center gap-3">
            {/* The prototype's second Save (`prefSaveStyle`) greys itself out on a condition it does
            not act on, which entry 130 of `handoff-corrections.md` rules a defect. This is the
            prototype's pill with a real disabled state: nothing to save, nothing to press. */}
            <Button
              type="submit"
              disabled={!dirty || save.isPending}
              className="h-auto rounded-pill px-[26px] py-[13px] font-brand text-md font-bold"
            >
              {save.isPending
                ? 'Saving…'
                : (preferences.data.mounts.preferences.saveAction ?? 'Save')}
            </Button>
            {saved && !dirty ? (
              <span role="status" className="text-xs font-semibold text-text-subtle">
                {preferences.data.mounts.preferences.savedNotice}
              </span>
            ) : null}
            {save.isError ? (
              <span role="alert" className="text-xs font-semibold text-destructive">
                That did not save. Try again.
              </span>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
